import React, {
  AriaRole,
  Children,
  forwardRef,
  LegacyRef,
  ReactNode,
} from 'react';
import clsx from 'clsx';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { AriaLabelingProps } from '@react-types/shared';
import styles from '@danske/sapphire-css/components/list/list.module.css';

import { ListContext } from './ListContext';
import { ListItem, ListItemText, Skeleton } from '../..';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
  withTruthyZero as hasAllowedCustomSidePadding,
} from '../../utils';
import { AriaLabelingPropsKeys, GlobalDomAttributes } from '../../utils/types';

export interface ListProps
  extends Pick<AriaLabelingProps, AriaLabelingPropsKeys>,
    SapphireStyleProps,
    GlobalDomAttributes {
  /**
   * The items within the list.
   */
  children: ReactNode;
  /**
   * Sets HTML tag of outermost element.
   * @default "ul"
   */
  elementType?: 'ul' | 'ol' | 'div';

  /**
   * The ARIA role
   */
  role?: AriaRole;
  /**
   * Removes the divider after the last list item.
   * Useful when rendered in a container which already has borders.
   */
  hideLastDivider?: boolean;
  /** Loading state type */
  loadingState?: 'loadingMore' | 'loading';
  /** List Item placeholder that will be rendered until loadingState is removed */
  renderLoadingSkeleton?: (index: number) => React.ReactNode;
  /**
   * Defines how many ListItem placeholders should be placed.
   * @default 3
   */
  loadingSkeletonRowsCount?: number;
  /**
   * Add custom sized padding to the sides of the list items.
   * @example
   * ```tsx
   * <List sidePadding={tokens.size.spacingContainerHorizontalMd.value} hasNegativeSideMargin>...</List>
   * ```
   */
  sidePadding?: number | string;
  /**
   * Counter acts the side padding of the parent element, so that List is shifted to the left with same amount
   * and having side padding * 2 added to width – resulting in full width List.
   * Useful in scenarions where List is inside a padded container and desired layout has List going from edge to edge horizontally.
   * @example
   * ```tsx
   * <List hasNegativeSideMargin>...</List>
   * ```
   */
  hasNegativeSideMargin?: boolean;
}

// Only used by InteractiveList. Thus avoiding inclusion in public API.
interface ListPropsWithAriaBusy extends ListProps {
  'aria-busy'?: boolean;
}

/**
 * We need these DOM props to be allowed on list items in order to use the
 * `<List>` to also implement `<InteractiveList>`.
 *
 * `filterDOMProps` alone filters too much. But no `filterDOMProps` at all
 * allows for non-dom props in the dom.
 */
const allowedListItemDomProps = new Set([
  'aria-multiselectable',
  'role',
  'tabIndex',
  'onBlur',
  'onFocus',
  'onMouseDown',
  'onKeyDown',
  'onKeyDownCapture',
]);

export const List = forwardRef(function List(
  props: ListProps,
  ref: LegacyRef<HTMLOListElement>
) {
  useThemeCheck();

  const {
    children,
    elementType: WrapperElement = 'ul',
    hideLastDivider = false,
    loadingState,
    renderLoadingSkeleton = renderDefaultLoadingSkeleton,
    loadingSkeletonRowsCount = 3,
    sidePadding,
    hasNegativeSideMargin,
    ...otherProps
  } = props;
  const { styleProps } = useSapphireStyleProps(otherProps);

  const content = renderListContent(
    renderLoadingSkeleton,
    loadingSkeletonRowsCount,
    loadingState,
    children
  );

  return (
    <ListContext.Provider value={{ sidePadding }}>
      <WrapperElement
        className={clsx(styles['sapphire-list'], styleProps.className, {
          [styles['sapphire-list--without-last-divider']]: hideLastDivider,
          [styles['sapphire-list--negative-margin-self']]:
            !hasAllowedCustomSidePadding(sidePadding) && hasNegativeSideMargin,
        })}
        style={{
          ...(hasAllowedCustomSidePadding(sidePadding) && hasNegativeSideMargin
            ? {
                marginLeft:
                  typeof sidePadding === 'string'
                    ? `calc(${sidePadding} * -1)`
                    : `-${sidePadding}px`,
                width: `calc(100% + calc(${sidePadding} * 2${
                  typeof sidePadding === 'string' ? '' : 'px'
                }))`,
              }
            : {}),
          ...styleProps.style,
        }}
        {...filterDOMProps(otherProps, {
          global: true,
          labelable: true,
          propNames: allowedListItemDomProps,
        })}
        aria-busy={
          ((props as ListPropsWithAriaBusy)['aria-busy'] || loadingState) &&
          true
        }
        ref={ref as LegacyRef<any>}
      >
        {content}
      </WrapperElement>
    </ListContext.Provider>
  );
});

export const renderDefaultLoadingSkeleton = (
  index: number
): React.JSX.Element => (
  <ListItem key={index}>
    <ListItem.Content
      contentLeft={
        <ListItemText
          primary={<Skeleton.Text widthType={'randomPx'} index={index} />}
        />
      }
      contentRight={
        <ListItemText
          primary={<Skeleton.Text widthType={'randomPx'} index={index} />}
          secondary={<Skeleton.Text widthType={'randomPx'} index={index} />}
        />
      }
    />
  </ListItem>
);

const from = (count = 0) => [...new Array(count)];
const renderListContent = (
  renderLoadingSkeleton: NonNullable<ListProps['renderLoadingSkeleton']>,
  loadingSkeletonRowsCount?: ListProps['loadingSkeletonRowsCount'],
  loadingState?: ListProps['loadingState'],
  children?: ReactNode
) => {
  if (!loadingState) {
    return children;
  }

  if (loadingState === 'loading') {
    const skeletonCount = Children.count(children) || loadingSkeletonRowsCount;
    return from(skeletonCount).map((_, i) => renderLoadingSkeleton(i));
  }
  if (loadingState === 'loadingMore') {
    const loadingIndicators = from(loadingSkeletonRowsCount).map((_, i) =>
      renderLoadingSkeleton(i)
    );

    return [Children.toArray(children), ...loadingIndicators];
  }
};
